import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IComponentAccessIPModals, IEftba024, IRefAccessIPModals } from "./interface";
import insertSchema from "./schemas";



const Modals: React.ForwardRefRenderFunction<IRefAccessIPModals, IComponentAccessIPModals> = ({
	paginationRef
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();

	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const [isAdd, setIsAdd] = useState(false);
	const [selectedRow, setSelecteRow] = useState<IEftba024 | undefined>(undefined);
	

	const { 
		handleSubmit, 
		register, 
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors
	} = useForm();


	const submitAddOrChange = async (data: any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();

			await insertSchema(translate).validate({...data}, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "AccessIp/Insert",
					body:  {
						...data
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "AccessIp/Update",
					body: {
						...data,
						efa024Id: selectedRow?.efa024Id
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Endereço IP adicionado com sucesso") : translate("Endereço IP alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err: any){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;
			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "invalidIpAddress"){
				addToast({
					title: translate("Erro"),
					description: translate("Endereço IP inválido"),
					color: "red"
				});
			}else{
				standardErrorMessages(err, isAdd ? translate("Erro ao adicionar endereço IP") : translate("Erro ao alterar endereço IP"), addToast);
			}

		}
	};

	const registerIP = async (data: any, callback?: Function) => {
		try{
			await RCCServicesApi.postRequest({
				method: "AccessIp/Insert",
				body:  {
					...data
				}
			});
			if(callback)
				callback();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar endereço IP"), addToast);
		}
	};

	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelecteRow(undefined);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IEftba024) => {
		reset();
		setIsAdd(false);
		setSelecteRow(row);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonDelete = (row: IEftba024) => {
		confirm(
			translate("Exclusão de endereço IP"),
			`${translate("Deseja realmente excluir o endereço IP")}: ${row.efa024Ip}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "AccessIp/Delete",
					urlParameters: {
						id: row.efa024Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Endereço IP excluído com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, "Erro ao excluir endereço IP", addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete,
		registerIP
	}));

	return (
		<>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar IP de acesso") : translate("Alterar IP de acesso"),
					subtitle: !isAdd ? `${translate("IP de acesso")}: ${selectedRow?.efa024Ip}`: "",
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={`${ModalSide} height: 40%; min-height: 400px;`}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCInput
						register={register}
						label={translate("IP de acesso")}
						name="efa024Ip"
						required
						maxLength={15}
						defaultValue={selectedRow?.efa024Ip}
						error={errors.efa024Ip}
					/>
					<RCCTextArea 
						register={register}
						label={translate("Observação")}
						name="efa024Observacao"
						maxLength={400}
						defaultValue={selectedRow?.efa024Observacao}
					/>
				</RCCGrid>
			</Modal>
		</>
	);


};

export default forwardRef(Modals);
